import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  UserPlus,
  ArrowRight,
  Users,
  Star,
  DollarSign,
  Zap,
} from "lucide-react";

const InviteLink = () => {
  const { affiliateName } = useParams();
  const navigate = useNavigate();
  const [isValidAffiliate, setIsValidAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateAffiliate = async () => {
      try {
        // Check if the affiliate exists in our system
        const { data, error } = await supabase
          .from("affiliate_entity")
          .select("name")
          .eq("name", affiliateName)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("Error checking affiliate:", error);
          setIsValidAffiliate(false);
        } else if (data) {
          setIsValidAffiliate(true);
          // Store referral code in localStorage for registration
          localStorage.setItem("referralCode", affiliateName);
        } else {
          setIsValidAffiliate(false);
        }
      } catch (error) {
        console.error("Error validating affiliate:", error);
        setIsValidAffiliate(false);
      } finally {
        setLoading(false);
      }
    };

    if (affiliateName) {
      validateAffiliate();
    } else {
      setIsValidAffiliate(false);
      setLoading(false);
    }
  }, [affiliateName]);

  const handleSignUp = () => {
    // Navigate to registration with referral parameter
    navigate(`/register?ref=${encodeURIComponent(affiliateName)}`);
  };

  const handleLogin = () => {
    // Navigate to login if they already have an account
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!isValidAffiliate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Invalid Invite Link
          </h1>
          <p className="text-gray-600 mb-6">
            This invitation link is not valid or has expired. Please contact the
            person who invited you for a new link.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition-colors"
          >
            Sign Up Anyway
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Header */}
          <div className="mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Users className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              You've Been Invited!
            </h1>
            <p className="text-xl text-gray-600">
              <span className="font-semibold text-purple-600 capitalize">
                {affiliateName}
              </span>{" "}
              has invited you to join our affiliate platform
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-xl p-6 shadow-md border border-purple-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Earn Commissions
              </h3>
              <p className="text-gray-600 text-sm">
                Start earning from day one with our tiered commission system
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-indigo-100">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Zap className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Real-time Analytics
              </h3>
              <p className="text-gray-600 text-sm">
                Track your performance with detailed analytics and insights
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-green-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Star className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Referral Rewards
              </h3>
              <p className="text-gray-600 text-sm">
                Earn 10% bonus on referred affiliates' earnings
              </p>
            </div>
          </div>

          {/* Special Offer */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 mb-8 text-white">
            <h3 className="text-xl font-bold mb-2">
              🎉 Special Invitation Bonus
            </h3>
            <p className="text-purple-100">
              Join through this invite link and your referrer{" "}
              <span className="font-semibold">{affiliateName}</span> will earn a
              10% bonus on your future earnings!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleSignUp}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              Get Started - Sign Up
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>

            <button
              onClick={handleLogin}
              className="inline-flex items-center px-8 py-4 border-2 border-purple-600 text-lg font-medium rounded-xl text-purple-600 bg-white hover:bg-purple-50 transition-colors"
            >
              Already have an account? Sign In
            </button>
          </div>

          {/* Footer Note */}
          <p className="text-sm text-gray-500 mt-8">
            By signing up, you'll be automatically connected to {affiliateName}{" "}
            as your referrer
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteLink;
